import React, { useCallback, useContext, useMemo, useRef } from "react";
import {
  Platform,
  ScrollView,
  StyleSheet,
  TouchableWithoutFeedback,
  useWindowDimensions,
  View,
} from "react-native";
import { useTheme } from "react-native-elements";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { clearFlag, hasFlag, setFlag } from "../../backend/Flags";
import { PlayerContext, PlayerContextState } from "../../backend/PlayerContext";
import InventoryItems from "../inventory/InventoryItems";
import SpellsContainer from "../spells/SpellsContainer";
import { AccessibilityContext } from "../utility/AccessibilityContext";
import { KeyboardListener } from "../utility/KeyboardListener";
import { Text } from "../utility/Text";
import { UIContext, useUIStateFromContext } from "../utility/UIContext";
import { VisibilityContext } from "../utility/VisibilityContext";
import { LoadoutsContainer } from "./LoadoutsContainer";
import { QuickbarContainer } from "./QuickbarContainer";

export function ExplorationBottomComponent() {
  const uiContext = useContext(UIContext);
  const [openedTab, setOpenedTab] = useUIStateFromContext(
    uiContext,
    "exploration_bottom_component_tab",
    "",
  );
  const theme = useTheme();
  const visible = useContext(VisibilityContext);
  const { height, width } = useWindowDimensions();
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;
  const isBottomMenuEnabled = playerContext.global.enableBottomMenu;

  const insets = useSafeAreaInsets();

  const heightWithoutBottomMenu =
    height -
    insets.top -
    insets.bottom -
    (isBottomMenuEnabled && width < 640 ? 48 : 0);

  const accessibilityContext = useContext(AccessibilityContext);
  const isPaused = hasFlag(playerContext, "combat_paused_magic_tap");

  const onPause = useCallback(() => {
    playerContextRef.current.apply((state: PlayerContextState) =>
      isPaused
        ? clearFlag("combat_paused_magic_tap")(state)
        : setFlag("combat_paused_magic_tap")(state),
    );
    accessibilityContext.announceForAccessibility(
      isPaused ? "Resuming Exploration" : "Pausing Exploration",
    );
  }, [accessibilityContext, isPaused]);

  const onKeyboard = useCallback(
    (ev: KeyboardEvent) => {
      if (visible && ev.shiftKey && ev.ctrlKey && ev.key == "X") {
        onPause();
      }
    },
    [onPause, visible],
  );

  return useMemo(
    () => (
      <View>
        <View
          style={[
            styles.mainComponent,
            { backgroundColor: theme.theme.colors.grey5 },
            !openedTab && { display: "none" },
          ]}
        >
          {openedTab == "Inventory" && (
            <ScrollView
              style={[
                styles.mainScrollView,
                {
                  height: Math.max(
                    Math.min(heightWithoutBottomMenu - 492, 350),
                    168,
                  ),
                },
              ]}
              nestedScrollEnabled={true}
            >
              <View
                style={styles.accessibilityHidden}
                accessible={true}
                accessibilityLabel="Inventory Tab"
                accessibilityRole="header"
                accessibilityElementsHidden={false}
              />
              <InventoryItems
                disableSelling={true}
                showQuickbar={true}
                overrideMaxHeight={138}
              />
            </ScrollView>
          )}
          <ScrollView
            style={[
              styles.mainScrollView,
              {
                height: Math.max(
                  Math.min(heightWithoutBottomMenu - 492, 350),
                  168,
                ),
              },
              openedTab != "Spells" && { display: "none" },
            ]}
          >
            <View
              style={styles.accessibilityHidden}
              accessible={true}
              accessibilityLabel="Spells Tab"
              accessibilityRole="header"
              accessibilityElementsHidden={false}
            />
            <SpellsContainer
              uiKey="ExplorationBottomContainer"
              area="Exploration"
              hideRecent={true}
              showAddToQuickbar={true}
              onlyBySchool={true}
              hideTitle={true}
              isVisible={openedTab == "Spells"}
            />
          </ScrollView>
          {openedTab == "Quickbar" && (
            <View
              style={[
                styles.quickbarScrollView,
                {
                  height: Math.max(
                    Math.min(heightWithoutBottomMenu - 492, 350),
                    150,
                  ),
                },
              ]}
            >
              <View
                style={styles.accessibilityHidden}
                accessible={true}
                accessibilityLabel="Quickbar Tab"
                accessibilityRole="header"
                accessibilityElementsHidden={false}
              />
              <QuickbarContainer />
            </View>
          )}
          {openedTab == "Loadouts" && (
            <ScrollView
              style={[
                styles.mainScrollView,
                {
                  height: Math.max(
                    Math.min(heightWithoutBottomMenu - 492, 350),
                    150,
                  ),
                },
                openedTab != "Loadouts" && { display: "none" },
              ]}
            >
              <View
                style={styles.accessibilityHidden}
                accessible={true}
                accessibilityLabel="Loadouts Tab"
                accessibilityRole="header"
                accessibilityElementsHidden={false}
              />
              <LoadoutsContainer />
            </ScrollView>
          )}
        </View>
        <View style={styles.tabsContainer}>
          <View
            style={styles.accessibilityHidden}
            accessible={true}
            accessibilityLabel="Exploration Tabs"
            accessibilityRole="header"
            accessibilityElementsHidden={false}
          />
          <TouchableWithoutFeedback
            onPress={onPause}
            accessibilityElementsHidden={false}
          >
            <View
              accessibilityLabel={
                isPaused
                  ? `Resume Exploration ${Platform.OS === "web"
                    ? "(Shortcut Control + Shift + X)"
                    : ""
                  }`
                  : `Pause Exploration ${Platform.OS === "web"
                    ? "(Shortcut Control + Shift + X)"
                    : ""
                  }`
              }
              accessibilityRole="button"
              style={[styles.accessibilityHidden, { left: 1 }]}
            />
          </TouchableWithoutFeedback>
          <TouchableWithoutFeedback
            onPress={() => {
              setOpenedTab("");
            }}
            accessibilityElementsHidden={false}
          >
            <View
              accessibilityLabel="Exploration Log - Exploration"
              accessibilityRole="tab"
              style={[styles.accessibilityHidden, { left: 2 }]}
            />
          </TouchableWithoutFeedback>
          <KeyboardListener callback={onKeyboard} />
          <TouchableWithoutFeedback
            onPress={() => {
              if (openedTab != "Inventory") {
                setOpenedTab("Inventory");
              } else {
                setOpenedTab("");
              }
            }}
            accessibilityLabel="Items - Exploration"
            accessibilityRole="tab"
          >
            <View
              style={[
                styles.tab,
                { backgroundColor: theme.theme.colors.grey5 },
                openedTab == "Inventory" && styles.selectedTab,
              ]}
            >
              <Text h3>Items</Text>
            </View>
          </TouchableWithoutFeedback>
          <TouchableWithoutFeedback
            onPress={() => {
              if (openedTab != "Spells") {
                setOpenedTab("Spells");
              } else {
                setOpenedTab("");
              }
            }}
            accessibilityLabel="Spells - Exploration"
            accessibilityRole="tab"
          >
            <View
              style={[
                styles.tab,
                { backgroundColor: theme.theme.colors.grey5 },
                openedTab == "Spells" && styles.selectedTab,
              ]}
            >
              <Text h3>Spells</Text>
            </View>
          </TouchableWithoutFeedback>
          <TouchableWithoutFeedback
            onPress={() => {
              if (openedTab != "Loadouts") {
                setOpenedTab("Loadouts");
              } else {
                setOpenedTab("");
              }
            }}
            accessibilityLabel="Loadouts - Exploration"
            accessibilityRole="tab"
          >
            <View
              style={[
                styles.tab,
                { backgroundColor: theme.theme.colors.grey5 },
                openedTab == "Loadouts" && styles.selectedTab,
              ]}
            >
              <Text h3>Loadouts</Text>
            </View>
          </TouchableWithoutFeedback>
          <TouchableWithoutFeedback
            onPress={() => {
              if (openedTab != "Quickbar") {
                setOpenedTab("Quickbar");
              } else {
                setOpenedTab("");
              }
            }}
            accessibilityLabel="Quickbar - Exploration"
            accessibilityRole="tab"
          >
            <View
              style={[
                styles.tab,
                { backgroundColor: theme.theme.colors.grey5 },
                openedTab == "Quickbar" && styles.selectedTab,
              ]}
            >
              <Text h3>Quickbar</Text>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </View>
    ),
    [
      openedTab,
      setOpenedTab,
      theme,
      visible,
      heightWithoutBottomMenu,
      onPause,
      isPaused,
    ],
  );
}

const styles = StyleSheet.create({
  mainComponent: {
    padding: 8,
    marginHorizontal: 2,
    borderWidth: 0.5,
  },
  mainScrollView: { height: 296 },
  quickbarScrollView: { height: 226, overflow: "hidden" },
  tabsContainer: {
    marginHorizontal: 8,
    marginBottom: 8,
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-evenly",
  },
  tab: {
    borderWidth: 0.5,
    borderRadius: 8,
    padding: 8,
    top: -1,
    cursor: "pointer",
  },
  selectedTab: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderTopWidth: 0,
    paddingTop: 16,
    position: "relative",
  },
  accessibilityHidden: {
    width: 1,
    height: 1,
    position: "absolute",
    left: 0,
    top: 0,
  },
});
